import React, { useCallback, useMemo, useState } from 'react';
import NavigationTabs from 'routes/Submissions/SubmissionDetail/navigation-tab';
import SubmissionHeader from 'components/ui/submission-header';
import {
  constructSubmissionDetailPath,
  ENavigationPath,
  EPath,
} from 'constants/path';
import { EQueryKeys } from 'constants/query-keys';
import HomeLayout from 'layouts/home-layout';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { submissionApi, contractApi } from 'services/apis';
import { useTranslation } from 'react-i18next';
import {
  getComponentByTabId,
  getSubmissionConfigTabs,
  NUMBER_OF_SUBMISSION_HEADER_TABS,
} from './navigation-tab/submission-config-tabs';
import {
  BusinessLineResponseDtoNameEnum,
  ContractResponseDtoContractStatusEnum,
  SubmissionResponseDtoStatusEnum,
  UpdateSubmissionStatusPayloadDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import { isNumeric } from 'utils';
import UpdateSubmissionStatusModal from 'components/features/update-submission-status-modal';
import { UpdateSubmissionStatusModalValues } from 'components/features/update-submission-status-modal/types';
import { isNumber } from 'lodash';
import { useBinderDeploymentValidation } from 'hooks/useBinderDeploymentValidation';
import { ECurrencies } from 'constants/enums';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';

const SubmissionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { tabId, submissionId } = useParams<{
    tabId: string;
    submissionId: string;
  }>();
  const activeKey = tabId && isNumeric(tabId) ? Number(tabId) : tabId || 0;
  const { t } = useTranslation();
  const [
    isUpdateSubmissionStatusModalOpen,
    setIsUpdateSubmissionStatusModalOpen,
  ] = useState(false);

  const { data: submission, refetch } = useQuery(
    [EQueryKeys.SUBMISSION_BY_ID, submissionId, tabId],
    () =>
      submissionApi.findSubmissionById({
        submissionId: submissionId as string,
        includeInsured: true,
        includeUnderwriter: true,
        prefetchChangelogs: 1,
        includeFiles: true,
      }),
    {
      enabled: !!submissionId,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
    }
  );

  const { isReadyForReview } = useBinderDeploymentValidation(
    submissionId,
    submission?.originalCurrency
  );

  const { data: bindingData } = useQuery(
    [
      EQueryKeys.BINDING_DATA_FOR_SUBMISSION_DETAILS,
      submissionId,
      submission?.originalCurrency || ECurrencies.USD,
    ],
    () =>
      contractApi.getBindingData({
        getBindingDataQueryDto: {
          submissionId: submissionId as string,
          currency: submission?.originalCurrency || ECurrencies.USD,
        },
      }),
    {
      enabled: !!submissionId,
    }
  );

  const updateSubmissionStatusMutation = useMutation(
    (status: UpdateSubmissionStatusPayloadDtoStatusEnum) =>
      submissionApi.updateSubmissionStatus({
        submissionId: submissionId as string,
        updateSubmissionStatusPayloadDto: {
          status,
        },
      }),
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const ActiveComponent = useMemo(
    () =>
      getComponentByTabId(
        activeKey,
        submission?.lineOfBusiness as BusinessLineResponseDtoNameEnum,
        t
      ),
    [activeKey, t]
  );

  const handleSubmissionStatusClick = useCallback(() => {
    setIsUpdateSubmissionStatusModalOpen(true);
  }, []);

  const handleUpdateSubmissionStatus = useCallback(
    (values: UpdateSubmissionStatusModalValues) => {
      if (
        values.status === UpdateSubmissionStatusPayloadDtoStatusEnum.Pending ||
        values.status === UpdateSubmissionStatusPayloadDtoStatusEnum.Quoted
      ) {
        setIsUpdateSubmissionStatusModalOpen(false);
        updateSubmissionStatusMutation.mutate(values.status);
      } else if (
        values.status === UpdateSubmissionStatusPayloadDtoStatusEnum.Complete
      ) {
        updateSubmissionStatusMutation.mutate(
          UpdateSubmissionStatusPayloadDtoStatusEnum.Complete
        );
        setIsUpdateSubmissionStatusModalOpen(false);
      } else {
        setIsUpdateSubmissionStatusModalOpen(false);
        navigate(
          constructSubmissionDetailPath(
            submissionId || '',
            EPath.CLOSE_SUBMISSION.replace('/', '')
          ),
          {
            state: {
              status: values.status,
            },
          }
        );
      }
    },
    [navigate, submissionId, updateSubmissionStatusMutation]
  );

  const isReadyToComplete = useMemo(() => {
    if (!bindingData) return false;
    const contracts = bindingData.flatMap((layer) => layer.contracts || []);
    if (contracts.length === 0) return false;

    if (
      contracts?.some(
        (contract) =>
          contract.contractStatus ===
            ContractResponseDtoContractStatusEnum.Pending ||
          contract.contractStatus ===
            ContractResponseDtoContractStatusEnum.Quoted
      )
    )
      return false;

    const boundContracts = contracts?.filter(
      (contract) =>
        contract.contractStatus === ContractResponseDtoContractStatusEnum.Bound
    );

    const allBoundContractsSlipUploaded = boundContracts.every(
      (c) => c.isSlipUploaded === true
    );

    return (
      allBoundContractsSlipUploaded &&
      submission?.status === SubmissionResponseDtoStatusEnum.Quoted
    );
  }, [bindingData, submission]);

  return (
    <HomeLayout
      title="Submission"
      backDestinationPath={ENavigationPath.DASHBOARD}
    >
      <div className="sticky top-0 z-10 bg-white">
        {submission && (
          <SubmissionHeader
            submission={submission}
            onStatusClick={
              submission.status === SubmissionResponseDtoStatusEnum.Cancelled ||
              submission.status === SubmissionResponseDtoStatusEnum.Complete
                ? undefined
                : handleSubmissionStatusClick
            }
          />
        )}
        {submissionId && (
          <UpdateSubmissionStatusModal
            open={isUpdateSubmissionStatusModalOpen}
            onCancel={() => setIsUpdateSubmissionStatusModalOpen(false)}
            onSubmit={handleUpdateSubmissionStatus}
            submissionStatus={submission?.status}
            isReadyToComplete={isReadyToComplete}
          />
        )}
        <NavigationTabs
          submissionId={submissionId}
          tabId={tabId}
          submissionStatus={submission?.status}
          businessLine={
            submission?.lineOfBusiness as BusinessLineResponseDtoNameEnum
          }
          isReadyForReview={isReadyForReview}
        />
      </div>
      {submission && (
        <div className="my-4 rounded-2xl bg-[#E3F2FB] p-3">
          <ActiveComponent
            submission={submission}
            includeBack={Number(activeKey) > 0}
            includeNext={
              Number(activeKey) <
              getSubmissionConfigTabs(
                undefined,
                submission.lineOfBusiness as BusinessLineResponseDtoNameEnum,
                isReadyForReview,
                t
              ).length -
                1
            }
            navigateBack={(step?: number) =>
              navigate(
                constructSubmissionDetailPath(
                  submission.submissionId,
                  String(
                    (Number(activeKey) -
                      (isNumber(step) ? step : 1) +
                      NUMBER_OF_SUBMISSION_HEADER_TABS) %
                      NUMBER_OF_SUBMISSION_HEADER_TABS
                  )
                )
              )
            }
            navigateNext={(step?: number) => {
              return navigate(
                constructSubmissionDetailPath(
                  submission.submissionId,
                  String(
                    (Number(activeKey) + (isNumber(step) ? step : 1)) %
                      NUMBER_OF_SUBMISSION_HEADER_TABS
                  )
                )
              );
            }}
          />
        </div>
      )}
    </HomeLayout>
  );
};

export default SubmissionDetail;
